import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Hand } from 'lucide-react';
import { projects } from '../mockData';

const ProjectCard = ({ project }) => {
  const images = useMemo(
    () => (project.images && project.images.length > 0 ? project.images : [project.image]),
    [project.images, project.image]
  );
  const hasGallery = images.length > 1;
  // Clone of the first image appended for a seamless infinite loop
  const displayImages = useMemo(
    () => (hasGallery ? [...images, images[0]] : images),
    [hasGallery, images]
  );
  const [current, setCurrent] = useState(0);
  const scrollRef = useRef(null);
  const settleTimer = useRef(null);

  // Preload all gallery images so swiping is instant (no flash/lag)
  useEffect(() => {
    if (!hasGallery) return;
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
    return () => clearTimeout(settleTimer.current);
  }, [hasGallery, images]);

  // Track which image is in view based on horizontal scroll position
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    setCurrent((prev) => (prev !== idx ? idx % images.length : prev % images.length));

    // When the scroll settles on the cloned first slide, jump back to the real
    // first slide instantly (visually identical, so the jump is invisible)
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      const node = scrollRef.current;
      if (!node) return;
      const settledIdx = Math.round(node.scrollLeft / node.clientWidth);
      const atSnapPoint = Math.abs(node.scrollLeft - settledIdx * node.clientWidth) < 2;
      if (settledIdx >= images.length && atSnapPoint) {
        node.style.scrollSnapType = 'none';
        node.scrollLeft = 0;
        requestAnimationFrame(() => {
          node.style.scrollSnapType = '';
          setCurrent(0);
        });
      }
    }, 120);
  }, [images.length]);

  const showText = !hasGallery || current === 0;

  // Mobile: axis-locked swipe — vertical gesture scrolls ONLY the page (photo frozen);
  // horizontal swipe ONLY flips to next/prev photo (no dragging of the card)
  const totalSlides = displayImages.length;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !hasGallery) return;
    let st = null;

    const onStart = (e) => {
      st = { x: e.touches[0].clientX, y: e.touches[0].clientY, mode: null };
    };
    const onMove = (e) => {
      if (!st) return;
      const t = e.touches[0];
      const dx = t.clientX - st.x;
      const dy = t.clientY - st.y;
      if (!st.mode) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        st.mode = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'h' : 'v';
      }
      if (st.mode === 'h') e.preventDefault();
    };
    const onEnd = (e) => {
      const s = st;
      st = null;
      if (!s || s.mode !== 'h') return;
      const dx = e.changedTouches[0].clientX - s.x;
      if (Math.abs(dx) < 50) return;
      const w = el.clientWidth;
      let idx = Math.round(el.scrollLeft / w) + (dx < 0 ? 1 : -1);
      idx = Math.max(0, Math.min(idx, totalSlides - 1));
      el.scrollTo({ left: idx * w, behavior: 'smooth' });
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [hasGallery, totalSlides]);

  return (
    <div
      data-testid={`project-card-${project.id}`}
      className="group relative overflow-hidden rounded-lg md:rounded-xl bg-gray-900 md:border md:border-gray-800/80 md:hover:border-[#cab28e]/40 md:hover:shadow-[0_16px_48px_rgba(0,0,0,0.7)] transition-all duration-300"
    >
      {/* Swipeable Images */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        data-testid={`project-${project.id}-scroller`}
        style={{ overscrollBehaviorX: 'none' }}
        className="aspect-[4/3] flex overflow-x-auto touch-pan-y md:touch-pan-x snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {displayImages.map((src, i) => (
          <div key={i} className="flex-[0_0_100%] w-full h-full snap-center snap-always">
            <img
              src={src}
              alt={project.title}
              draggable={false}
              className="w-full h-full object-cover select-none"
            />
          </div>
        ))}
      </div>

      {/* Overlay */}
      <div className={`absolute inset-0 pointer-events-none bg-gradient-to-t from-black via-black/50 to-transparent transition-opacity duration-300 ${showText ? 'opacity-60' : 'opacity-0'}`} />

      {/* Gallery Indicator (dots) */}
      {hasGallery && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex space-x-1.5 pointer-events-none">
          {images.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === current ? 'w-5 bg-[#cab28e]' : 'w-1.5 bg-white/60'
              }`}
            />
          ))}
        </div>
      )}

      {/* Photo counter + swipe indicator (desktop + mobile) */}
      {hasGallery && (
        <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/55 text-white text-xs font-medium backdrop-blur-sm pointer-events-none">
          <span>Povucite</span>
          <Hand className="w-3.5 h-3.5 text-[#cab28e] animate-swipe-hint" />
          <span>{current + 1}/{images.length}</span>
        </div>
      )}

      {/* Content */}
      <div className={`absolute bottom-0 left-0 right-0 p-6 pt-16 bg-gradient-to-t from-black via-black/90 to-transparent pointer-events-none transition-opacity duration-300 ${showText ? 'opacity-100' : 'opacity-0'}`}>
        <div className="mb-2">
          <span className="text-[#cab28e] text-xs font-semibold tracking-wider uppercase">
            {project.category}
          </span>
        </div>
        <h3 className="text-white text-xl md:text-2xl font-bold mb-2 tracking-wide">
          {project.title}
        </h3>
        <p className="text-gray-300 text-xs leading-relaxed">
          {project.description}
        </p>
      </div>
    </div>
  );
};

const Projects = () => {
  return (
    <section id="projects" className="py-12 md:py-16 bg-gradient-to-b from-black via-gray-900 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-12">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-0.5 bg-[#cab28e] mr-4" />
            <span className="text-[#cab28e] text-sm tracking-widest font-semibold">
              NAŠI PROJEKTI
            </span>
            <div className="w-16 h-0.5 bg-[#cab28e] ml-4" />
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight">
            ODABRANI <span className="text-[#cab28e]">RADOVI</span>
          </h2>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
